import React, { useEffect, useCallback, useMemo } from "react";
import container from "../../../../styles/Global.module.css";
import { Divider } from "@mui/material";
import ViewsCPS from "../../../others/ViewsCPS";
import { useLocation } from "react-router-dom";
import { fetchData } from "../../../../utils/ApiCall";
import BaseBoard from "../../../global/BoardTemplate/BaseBoard.jsx";
import { useBoardStatePreset, useUsers } from "../../../../hooks/boards";
import { separateMasterCpsItems } from "../../../../utils/boards/boardFilters";
import { getMasterCpsColumns } from "../../../../config/columns/masterCpsColumns";
import { 
  mapFilterOptions, 
  mapFilterOptionsCode, 
  OptionsFromColumns 
} from "../../../../utils/masterCpsHelpers";
import { useMasterCpsEdit } from "../../../../hooks/useMasterCpsEdit";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const AlunaMasterCPS = () => {
  const apiUrl = `${backendUrl}/api/master-cps/items`;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tableByParams = searchParams.get("table");

  // State management
  const [state, updateState] = useBoardStatePreset('master-cps', {
    additionalState: {
      offerID: [],
      inactive: [],
      pixelIntegration: [],
      updateRows: [],
      status: tableByParams ?? "Offer ID",
      columnsFilter: {
        revenueRetargeting: false,
        id: false,
      },
      initialViews: true,
    }
  });
  
  useUsers({
    onSuccess: (userData) => updateState({ users: userData })
  });

  // Load data directly
  useEffect(() => {
    const loadData = async () => {
      if (apiUrl) {
        try {
          const data = await fetchData(apiUrl);
          const separated = separateMasterCpsItems(data);
          updateState({
            offerID: separated.offerID,
            inactive: separated.inactive,
            pixelIntegration: separated.pixelIntegration,
          });
        } catch (error) {
          console.error("Error loading data:", error);
        }
      }
    };
    loadData();
  }, [apiUrl]);

  // Dummy function for BaseBoard
  const stableSetGroupItems = useCallback(() => {
    // Do nothing - we handle data loading ourselves
  }, []);

  const getGroupRows = useCallback((groupName) => {
    return groupName === "Offer ID"
      ? state.offerID
      : groupName === "Inactive"
      ? state.inactive
      : groupName === "Pixel Integration"
      ? state.pixelIntegration
      : [];
  }, [state.offerID, state.inactive, state.pixelIntegration]);

  // Use custom hooks for edit logic
  const { handleEditResponseChange, updateBoard } = useMasterCpsEdit(
    state, 
    updateState, 
    location.pathname
  );

  // Get columns from external config
  const columns = useMemo(() => {
    return getMasterCpsColumns(
      state,
      { handleEditResponseChange, updateState },
      { 
        mapFilterOptions, 
        mapFilterOptionsCode, 
        OptionsFromColumns
      },
      { backendUrl, pathname: location.pathname }
    );
  }, [state, handleEditResponseChange, updateState, backendUrl, location.pathname]);

  return (
    <div className={container.globalContainer}>
      <ViewsCPS
        setViewFilter={(viewFilter) => updateState({ viewFilter })}
        setColumnsFilter={(columnsFilter) => updateState({ columnsFilter })}
        setTables={(groups) => updateState({ groups })}
        initialViews={state.initialViews}
        setSelectedView={(selectedView) => updateState({ selectedView })}
        selectedView={state.selectedView}
        setPinnedColumns={(pinnedColumns) => updateState({ pinnedColumns })}
      />
      <Divider sx={{ marginBottom: "20px" }} />
      <BaseBoard
        columns={columns}
        groups={["Offer ID", "Inactive", "Pixel Integration"]}
        getGroupRows={getGroupRows}
        setGroupItems={stableSetGroupItems}
        apiUrl={null}
        status={state.status}
        setStatus={(status) => updateState({ status })}
        updateBoard={updateBoard}
        updateRows={state.updateRows}
        setSelectionRow={useCallback((selectionRow) => updateState({ selectionRow }), [])}
        selectionRow={state.selectionRow}
        setViewFilter={(viewFilter) => updateState({ viewFilter })}
        viewFilter={state.viewFilter}
        columnsFilter={state.columnsFilter}
        setColumnsFilter={(columnsFilter) => updateState({ columnsFilter })}
        pinnedColumns={state.pinnedColumns}
        setPinnedColumns={(pinnedColumns) => updateState({ pinnedColumns })}
        checkboxSelection={true}
        height={"80vh"}
        maxHeight={"80vh"}
      />
    </div>
  );
};

export default AlunaMasterCPS;
